import {
  cleanRut,
  formatRut,
  sanitizeRut,
  validateRut
} from '@smuchile/pkg-unimarc-components/utils'
import {
  FormField,
  Form as FormContent,
  ValidationsType
} from 'shared/interfaces/Form'

export type SectionValues = Record<string, string | boolean>
export type FormValues = Record<string, SectionValues>

export const isTeletonSection = (sectionId: string) => {
  return sectionId?.toLowerCase() === 'formularioteleton'
}

export const withContactabilityLimits = (field: FormField): FormField => {
  if (field.formfieldId !== 'custom1' && field.formfieldId !== 'custom2')
    return field
  return { ...field, maxValue: Math.min(field.maxValue || 2000, 2000) }
}

export const buildTeletonFormData = (form: FormContent, values: FormValues) => {
  const sectionId = form.formSections.find((section) => {
    return isTeletonSection(section.sectionId)
  })?.sectionId
  const fields = (sectionId && values[sectionId]) || {}
  const { custom1, custom2, lastName, ...rest } = fields
  const name = [fields.name, lastName].filter(Boolean).join(' ')
  const phone = String(fields.phone || '')

  return {
    ...rest,
    ...(name && { name }),
    campaignId: form.formId,
    ...(fields.rut && { rut: cleanRut(String(fields.rut)) }),
    ...(phone && { phone: phone.startsWith('+') ? phone : `+569${phone}` }),
    ...(typeof custom1 === 'string' && custom1.trim() && { custom1 }),
    ...(typeof custom2 === 'string' && custom2.trim() && { custom2 }),
    source: 'web'
  }
}

export const validateError = (
  formField: FormField,
  formFieldValue: unknown
) => {
  const value =
    formField?.validations === ValidationsType.RUT
      ? cleanRut(formFieldValue as string)
      : formFieldValue

  if (formField?.validations === ValidationsType.Email) {
    const emailRegex =
      /^(?:[a-zA-Z0-9._%+-]+)@(?:[a-zA-Z0-9.-]+\.)[a-zA-Z]{2,}$/
    return !emailRegex.test(value as string)
  }

  if (formField?.validations === ValidationsType.RUT) {
    return !validateRut(value as string)
  }

  return Boolean(
    (formField?.minValue && (value as string)?.length < formField.minValue) ||
      (formField?.maxValue && (value as string)?.length > formField.maxValue)
  )
}

export const sanitizeInputValue = (
  value: string,
  formField: FormField
): string => {
  if (!value) return ''

  switch (formField.validations) {
    case ValidationsType.RUT:
      value =
        cleanRut(value).length < 10 ? formatRut(sanitizeRut(value)) : value
      break
    case ValidationsType.OnlyNumbers:
    case ValidationsType.PhoneNumber:
      value = value.replace(/\D/g, '')
      break
    case ValidationsType.OnlyText:
      value = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '')
      break
  }

  if (
    formField.maxValue &&
    value.length > formField.maxValue &&
    formField.validations !== ValidationsType.RUT
  ) {
    value = value.slice(0, formField.maxValue)
  }

  return value
}
